import { Alert, Box, Button, Divider, IconButton, Modal } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { DetailsImageResponse } from "../models/ResponseModels/DetailsImageResponse";
import ErrorsString from "../ErrorsString";
import config from "../config";
import CloseIcon from "@mui/icons-material/Close";
import ImageCropDownload from "./ImageCropDownload";
import Spinner from "./Spinner";
import { PostShareModel } from "../models/PostShareModel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { saveAs } from "file-saver";
import { GetImageDetailsModel } from "../models/GetImageDetailsModel";
import StarIcon from "@mui/icons-material/Star";
import TagList from "./TagList";
import { logDownload } from "../Utilities";

interface Props {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  uuid: string | null;
  setSelectedUuid: React.Dispatch<React.SetStateAction<string>>;
}

export default function ImageDetailsModal(props: Props) {
  const [spinnerVisibility, setSpinnerVisibility] = useState(false);
  let [alertMessage, setAlertMessage] = useState<string>("");
  let [showModalAlert, setshowModalAlert] = useState<boolean>(false);
  let [alertShareMessage, setAlertShareMessage] = useState<string>("");
  let [showModalAlertShare, setshowModalAlertShare] = useState<boolean>(false);
  let [ready, setReady] = useState<boolean>(false);
  let [details, setDetails] = useState<DetailsImageResponse | null>(null);
  let [urlToShare, setUrlToShare] = useState<string | null>(null);
  let [isCopied, setIsCopied] = useState<boolean>(false);

  const baseUrl = window.location.origin;
  const textRef = useRef(null);

  useEffect(() => {
    setshowModalAlert(false);
    getImageDetails();
  }, [props.uuid]);

  function handleClose() {
    setshowModalAlert(false);
    setReady(false);
    setDetails(null);
    setUrlToShare(null);
    props.setSelectedUuid(null);

    props.setOpenModal(false);
  }

  function getImageDetails() {
    if (props.uuid != null) {
      window.scrollTo(0, 0);
      setSpinnerVisibility(true);

      console.log("Starting API call GetImageDetailsModel...");
      (async () => {
        const response = await GetImageDetailsModel(props.uuid);
        // console.log( "GetImageDetailsModel response: " + JSON.stringify(response)  );

        const responseData = response?.data;
        const statusCode = response?.status;

        if (responseData === undefined) {
          setshowModalAlert(true);
          setAlertMessage(ErrorsString.errorMessageGeneric);
          setSpinnerVisibility(false);
          return;
        }

        if (statusCode !== 200) {
          setshowModalAlert(true);
          setAlertMessage(ErrorsString.errorMessageGeneric);
          setSpinnerVisibility(false);
          return;
        } else {
          setshowModalAlert(false);
          setDetails(responseData);
          setSpinnerVisibility(false);
        }

        setReady(true);
      })();
    }
  }

  function share(imageUuid) {
    console.log("Starting API call PostShareModel...");
    (async () => {
      const response = await PostShareModel(imageUuid);
      //console.log("PostShareModel response: " + JSON.stringify(response));

      const responseData = response?.data;
      const statusCode = response?.status;

      if (responseData === undefined) {
        setshowModalAlertShare(true);
        setAlertShareMessage("Error during share");
        return;
      }

      if (statusCode !== 200) {
        setshowModalAlertShare(true);
        setAlertShareMessage("Error during share");
        return;
      } else {
        const encryptedString = responseData.encryptedString;
        const urlToShare = baseUrl + "/public/landing/" + encryptedString;
        //console.log("urlToShare: "+urlToShare);
        setUrlToShare(urlToShare);
        setIsCopied(false);
      }
    })();
  }

  function copyToClipboard() {
    if (textRef.current) {
      navigator.clipboard
        .writeText(textRef.current.querySelector("#shared-url").textContent)
        .then(() => {
          setIsCopied(true);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }
  }

  function downloadOriginalImage() {
    if (details.essence) {
      const url =
        config.cloudfrontImgUrl + "" + details.essence + config.showImagesQuery;
      let imageFileName = "";
      if (details.imageTitle) {
        imageFileName = details.imageTitle;
      } else {
        const today = new Date();
        const formattedDate = today.toLocaleDateString();
        imageFileName = "image" + formattedDate;
      }

      const fileExtension = details.fileExtension
        ? details.fileExtension
        : "jpg";
      imageFileName = imageFileName + "." + fileExtension;

      saveAs(url, imageFileName);

      logDownload(details.uuid, null);
    }
  }

  return (
    <>
      <Spinner visibility={spinnerVisibility} />
      {ready && (
        <Modal
          open={props.openModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            className="
            absolute 
            top-1/2 
            left-1/2 
            -translate-x-1/2 
            -translate-y-1/2             
            w-[90%]
            bg-white              
            px-0
            py-2
            md:p-4
            rounded
            "
          >
            <>
              <div
                id="modal-header"
                className=" h-8 w-full border-b border-gray-300 flex justify-between"
              >
                {details && (
                  <div>
                    <div className="font-skyBold text-xl line-clamp-1">
                      {details.woTitle}
                      <span className="font-skyMedium  text-gray-500 ">
                        {" - "}
                        {details.imageTitle}
                      </span>
                    </div>
                  </div>
                )}
                <div className="float-right">
                  <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              {showModalAlert && <Alert severity="error">{alertMessage}</Alert>}
              {details && (
                <div
                  id="image-body"
                  style={{
                    maxHeight: "80vh",
                    overflow: "auto",
                  }}
                >
                  <div id="share-info" className="text-lg mt-3">
                    {showModalAlertShare ? (
                      <Alert severity="error">{alertShareMessage}</Alert>
                    ) : (
                      urlToShare && (
                        <div
                          ref={textRef}
                          onClick={copyToClipboard}
                          className=" font-skyRegular p-2 bg-blue-100 border border-blue-300 rounded-md cursor-pointer hover:bg-blue-200 transition-colors"
                          title="Click to copy url"
                        >
                          <ContentCopyIcon className="mr-4" />
                          <span id="shared-url">{urlToShare} </span>
                          <span
                            className={` float-right text-blue-500 font-skyBold  ${
                              isCopied ? "block" : "hidden"
                            } `}
                          >
                            copied
                          </span>
                        </div>
                      )
                    )}
                  </div>

                  <div
                    id="actions"
                    className="mb-4 mt-4 mr-3 float-right flex justify-end w-full"
                  >
                    {details.isDownloadable && (
                      <div className="mr-3">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => downloadOriginalImage()}
                        >
                          Download Original File
                        </Button>
                      </div>
                    )}

                    {details.isShareable && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => share(details.uuid)}
                      >
                        Share
                      </Button>
                    )}
                  </div>

                  <div id="image-details" className="flex w-full mt-4">
                    <div id="image-preview" className="p-4 w-1/2 w-3/4">
                      <img
                        src={
                          details.proxy != null
                            ? config.cloudfrontImgUrl +
                              "" +
                              details.proxy +
                              config.showImagesQuery
                            : config.placeholderImageUrl
                        }
                        alt={details.imageTitle}
                      />
                    </div>
                    <div id="image-info" className=" p-4 w-full">
                      <div
                        className={`mt-auto mb-auto text-xl flex items-end font-skyRegular ${
                          details.isGreenStar && details.isGreenStar == true
                            ? "visible"
                            : "invisible"
                        } `}
                      >
                        <StarIcon
                          style={{ color: "green", marginRight: "5px" }}
                        />{" "}
                        Recommended by Editorial
                      </div>
                      <div id="details" className=" mt-8">
                        <div className="p-2">
                          <div
                            id="title"
                            className={` text-xl mb-3 flex  ${
                              details.imageTitle != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Title:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.imageTitle}
                            </span>
                          </div>

                          <div
                            id="description"
                            className={` text-xl mb-3 flex ${
                              details.description != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Description:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.description}
                            </span>
                          </div>

                          <div
                            id="subject_person"
                            className={` text-xl mb-3 flex ${
                              details.person != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Person Shown:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.person}
                            </span>
                          </div>
                        </div>

                        <Divider />

                        <div className="p-2">
                          <div
                            id="work_order_title"
                            className={`text-xl mb-3 flex ${
                              details.woTitle != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Programme/Film Name:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.woTitle}
                            </span>
                          </div>

                          <div
                            id="channel"
                            className={`text-xl mb-3 flex ${
                              details.woChannel != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Channel:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.woChannel}
                            </span>
                          </div>

                          <div
                            id="series_number"
                            className={`text-xl mb-3 flex ${
                              details.seriesNumber != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Series number:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.seriesNumber}
                            </span>
                          </div>
                        </div>
                        <Divider />

                        <div className="p-2">
                          <div
                            id="type"
                            className={` text-xl mb-3 flex ${
                              details.imageType != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Element Type:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.imageType}
                            </span>
                          </div>

                          <div
                            id="file-name"
                            className={` text-xl mb-3 flex ${
                              details.fileName != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              File Name:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {details.fileName}
                            </span>
                          </div>

                          <div
                            id="file-Size"
                            className={` text-xl mb-3 flex ${
                              details.fileSizeBytes != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Size:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {" "}
                              {(details.fileSizeBytes / 1024).toFixed(2)}
                              {" KB "}
                            </span>
                          </div>

                          <div
                            id="file_extension"
                            className={` text-xl mb-3 flex ${
                              details.fileExtension != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Extension:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.fileExtension}
                            </span>
                          </div>
                        </div>

                        <Divider />

                        <div className="p-2">
                          <div
                            id="copyright"
                            className={` text-xl mb-3 flex ${
                              details.copyright != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Copyright:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.copyright}
                            </span>
                          </div>

                          <div
                            id="creator"
                            className={` text-xl mb-3 flex ${
                              details.creator != null ? "block" : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Creator:{" "}
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.creator}
                            </span>
                          </div>

                          {/* 
                          <div
                            id="tags"
                            className={` text-xl mb-3 flex  ${
                              details.tags != null &&
                              Object.keys(details.tags).length > 0
                                ? "block"
                                : "hidden"
                            } `}
                          >
                            <div className="-w-40  font-skyBold  -text-right">
                              Tags:
                            </div>
                            <span className="ml-2 font-skyRegular">
                              {details.tags ? (
                                <TagList tags={details.tags} />
                              ) : (
                                ""
                              )}
                            </span>
                          </div>
                        */}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider />

                  <div id="image-crops">
                    <ImageCropDownload
                      uuid={details.uuid}
                      savedCrops={details.imageCropInfo}
                      realtivePath={details.essence}
                      imageDetails={details.imageDetails}
                      imageName={details.imageTitle}
                      fileName={details.fileName}
                      isDownloadable={details.isDownloadable}
                    ></ImageCropDownload>
                  </div>
                </div>
              )}
            </>
          </Box>
        </Modal>
      )}
    </>
  );
}
